import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Avatar } from '../../../common/Avatar'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useModal } from '../../../../hooks/useModal'
import { DeleteBookingConfirmationModal } from '../../Facility/DeleteBookingConfirmationModal'
import { Card } from '../../../common/Card'
import { theme } from '../../../../theme/theme'
import { capitalize } from '../../../../helpers/string'
import { formatDateWithFormat } from '../../../../helpers/date'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { bookingQueries, deleteBooking } from '../../../../api/bookings'
import { ToastType } from '../../../common/Toast/toast-type'
import { IBookingModel } from '../../../../interfaces/models/bookings.interfaces'
import { facilityQueries } from '../../../../api/facilities'
import { getTimeSpanString } from '../../../../state/models/booking'

interface BookingListItemProps {
  booking: IBookingModel
}

export const BookingListItem = ({
  booking,
}: BookingListItemProps): React.ReactNode => {
  const isMobile = useIsMobile()
  const { translate } = useAppTranslation()
  const {
    show: showDeleteBookingModal,
    open: openDeleteBookingModal,
    close: closeDeleteBookingModal,
  } = useModal()

  const { setToastNotification } = useToastNotifications()
  const client = useQueryClient()
  const { mutate: mutateDeleteBooking, isPending } = useMutation({
    mutationFn: deleteBooking,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: [bookingQueries.scope] })
      closeDeleteBookingModal()
      setToastNotification(
        ToastType.SUCCESS,
        translate('facilityView.bookings.flashMessage.deleteBookingSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('facilityView.bookings.flashMessage.deleteBookingFailure')
      )
    },
  })
  const onDeleteBooking = async (): Promise<void> => {
    booking && mutateDeleteBooking(booking._id)
  }

  const { data: facility } = useQuery(
    facilityQueries.getFacility(booking.facilityId)
  )

  const timeSpanString =
    facility?.bookingGranularity === 'full-day'
      ? translate('facilityView.calendar.fullDay')
      : getTimeSpanString(booking)

  return (
    <>
      <Card className="p-5 hover:bg-neutral-96">
        {{
          body: (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {facility?.coverPhoto && (
                  <Avatar
                    url={facility.coverPhoto.url}
                    size={isMobile ? 32 : 48}
                  />
                )}
                <div className="ml-4 flex flex-col gap-[1px] text-left">
                  <p style={theme.textVariants.baseBold}>{facility?.name}</p>
                  <p
                    style={theme.textVariants.caption}
                    className="text-neutral-50"
                  >
                    {capitalize(
                      formatDateWithFormat(
                        new Date(booking.startDate),
                        isMobile ? 'd MMM YYY' : 'd MMMM YYY'
                      )
                    )}
                  </p>
                  <p
                    style={
                      isMobile
                        ? theme.textVariants.caption
                        : theme.textVariants.base
                    }
                  >
                    {timeSpanString}
                  </p>
                </div>
              </div>
              <Button
                label={translate('facilityView.bookings.buttons.deleteBooking')}
                variant={ButtonVariant.DANGER}
                onClick={openDeleteBookingModal}
              />
            </div>
          ),
        }}
      </Card>
      <DeleteBookingConfirmationModal
        loading={isPending}
        showModal={showDeleteBookingModal}
        closeModal={closeDeleteBookingModal}
        booking={booking}
        disableCloseOnConfirm
        onDelete={onDeleteBooking}
      />
    </>
  )
}
